/**
 * Dashboard Builder shell.
 *
 * Owns the working copy of the configuration and the step navigation; each step
 * is its own component, so this file stays a router rather than growing into a
 * monolith. Persistence goes through `saveDashboard`, which routes to the
 * guarded `moduleService.updateModule` — a user without edit rights on this
 * module cannot save, even by calling the service directly.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Check,
  Database,
  Eye,
  Filter,
  LayoutGrid,
  Rocket,
  Save,
  Undo2,
  Settings2,
} from 'lucide-react';
import type { AppModule } from '../types';
import Tooltip from '../components/glass/Tooltip';
import { useAuth } from '../auth/AuthContext';
import { ModulePermissionError } from '../auth/modulePermissions';
import DashboardDataSources from './DashboardDataSources';
import DashboardFilters from './DashboardFilters';
import DashboardIndicators from './DashboardIndicators';
import DashboardLayout from './DashboardLayout';
import DashboardPreview from './DashboardPreview';
import DashboardSettings from './DashboardSettings';
import { getDashboardConfig, saveDashboard, validateDashboard } from './services/dashboardService';
import type { DashboardConfig } from './types/dashboardTypes';

type Step = 'sources' | 'indicators' | 'selectors' | 'layout' | 'settings';

const STEPS: { id: Step; label: string; icon: typeof Database }[] = [
  { id: 'sources', label: 'Data Sources', icon: Database },
  { id: 'indicators', label: 'Widgets', icon: BarChart3 },
  { id: 'selectors', label: 'Filters', icon: Filter },
  { id: 'layout', label: 'Layout', icon: LayoutGrid },
  { id: 'settings', label: 'Settings', icon: Settings2 },
];

interface Props {
  module: AppModule;
  onBack: () => void;
  /** Called after a successful save so the portal can refresh its module list. */
  onSaved: (module: AppModule) => void;
  /** Called after Save & Launch to open the published viewer. */
  onLaunch: (module: AppModule) => void;
}

export default function DashboardBuilder({ module, onBack, onSaved, onLaunch }: Props) {
  const { currentUser } = useAuth();

  const [title, setTitle] = useState(module.title);
  const [config, setConfigState] = useState<DashboardConfig>(() => getDashboardConfig(module));

  /**
   * Undo history.
   *
   * Every edit that reaches `setConfig` pushes the PREVIOUS config, so a widget
   * dragged somewhere by accident can be put back without the admin having to
   * remember exactly where it was. Capped, because a dashboard config holds its
   * widgets and an unbounded stack would grow with every drag.
   */
  const [history, setHistory] = useState<DashboardConfig[]>([]);
  const HISTORY_LIMIT = 40;

  const setConfig = useCallback(
    (next: DashboardConfig | ((current: DashboardConfig) => DashboardConfig)) => {
      setConfigState((current) => {
        const resolved = typeof next === 'function' ? next(current) : next;
        // Identical objects mean a no-op re-render, not an edit worth undoing.
        if (resolved === current) return current;
        setHistory((stack) => [...stack, current].slice(-HISTORY_LIMIT));
        return resolved;
      });
    },
    [],
  );

  const undo = useCallback(() => {
    setHistory((stack) => {
      if (stack.length === 0) return stack;
      const previous = stack[stack.length - 1];
      setConfigState(previous);
      return stack.slice(0, -1);
    });
  }, []);

  /* Ctrl/Cmd+Z, because that is the first thing anyone tries. Ignored while a
     text field has focus, where the browser's own undo is the right one. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'z') return;
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      event.preventDefault();
      undo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [undo]);
  const [step, setStep] = useState<Step>('sources');
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [launchErrors, setLaunchErrors] = useState<string[] | null>(null);

  const validation = useMemo(() => validateDashboard(config), [config]);

  const persist = async (publish: boolean): Promise<AppModule | null> => {
    try {
      const saved = await saveDashboard(
        currentUser,
        { ...module, title: title.trim() || module.name },
        config,
        { publish },
      );
      setNotice({ kind: 'ok', text: publish ? 'Dashboard published.' : 'Dashboard saved.' });
      onSaved(saved);
      return saved;
    } catch (error) {
      if (error instanceof ModulePermissionError) {
        console.error('[dashboard] save denied:', error.message);
        setNotice({ kind: 'error', text: error.message });
        return null;
      }
      console.error('[dashboard] save failed', error);
      setNotice({ kind: 'error', text: 'Could not save this dashboard.' });
      return null;
    }
  };

  const handleSave = () => {
    setLaunchErrors(null);
    void persist(false);
  };

  const handleSaveAndLaunch = async () => {
    // Publishing is refused while any widget would render broken for an end
    // user, who has no controls to fix it.
    if (!validation.ok) {
      setLaunchErrors(validation.errors);
      setNotice(null);
      return;
    }
    setLaunchErrors(null);
    // Awaited so a refused save never navigates on to the viewer, which would
    // show the previously published version and read as success.
    const saved = await persist(true);
    if (saved) onLaunch(saved);
  };

  return (
    <div
      className="flex h-screen flex-col overflow-hidden"
      style={{
        background:
          // Token, so the builder follows the application theme. See glass.css.
          'var(--bv-ground)',
      }}
    >
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header className="gl-panel flex flex-shrink-0 flex-wrap items-center gap-3 rounded-none border-x-0 border-t-0 px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to applications"
          className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>

        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="flex-shrink-0 rounded-md bg-blue-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-blue-700">
            Dashboard
          </span>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            aria-label="Dashboard name"
            className="min-w-0 flex-1 rounded-lg border border-transparent bg-slate-50 px-2.5 py-1.5 text-sm font-medium text-slate-800 outline-none transition hover:border-slate-200 focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <Tooltip content="Undo the last change — including a widget moved by accident. Ctrl+Z also works.">
            <button
              type="button"
              onClick={undo}
              disabled={history.length === 0}
              aria-label="Undo"
              className="gl-btn gl-btn--secondary px-3 py-2 text-sm disabled:opacity-40"
            >
              <Undo2 className="h-4 w-4" />
              Undo
            </button>
          </Tooltip>
          <button
            type="button"
            onClick={() => setPreviewing(true)}
            className="gl-btn gl-btn--secondary px-3 py-2 text-sm"
          >
            <Eye className="h-4 w-4" />
            Preview
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="gl-btn gl-btn--secondary px-3 py-2 text-sm"
          >
            <Save className="h-4 w-4" />
            Save
          </button>
          <button
            type="button"
            onClick={handleSaveAndLaunch}
            className="gl-btn gl-btn--primary px-3.5 py-2 text-sm"
          >
            <Rocket className="h-4 w-4" />
            Save &amp; Launch
          </button>
        </div>
      </header>

      {/* ── Step navigation ─────────────────────────────────────────────── */}
      <nav className="gl-panel flex flex-shrink-0 gap-1 overflow-x-auto rounded-none border-x-0 border-t-0 px-4">
        {STEPS.map((entry) => {
          const Icon = entry.icon;
          const active = step === entry.id;
          const count =
            entry.id === 'sources'
              ? config.dataSources.length
              : entry.id === 'indicators'
                ? config.widgets.length
                : entry.id === 'selectors'
                  ? config.selectors.length
                  : null;

          return (
            <button
              key={entry.id}
              type="button"
              onClick={() => setStep(entry.id)}
              className={`relative flex flex-shrink-0 items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition ${
                active ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              {entry.label}
              {count !== null && count > 0 && (
                <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-500">
                  {count}
                </span>
              )}
              {active && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-600" />}
            </button>
          );
        })}
      </nav>

      {/* ── Step body ───────────────────────────────────────────────────── */}
      <main className="min-h-0 flex-1 overflow-y-auto p-4">
        {launchErrors && launchErrors.length > 0 && (
          <div
            role="alert"
            className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
          >
            <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              Fix these before launching
            </p>
            <ul className="mt-1.5 space-y-0.5">
              {launchErrors.map((error) => (
                <li key={error} className="text-xs text-amber-800">
                  • {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {step === 'sources' && (
          <DashboardDataSources config={config} onChange={setConfig} moduleId={module.id} />
        )}

        {step === 'indicators' && (
          <DashboardIndicators
            config={config}
            onChange={setConfig}
            selectedWidgetId={selectedWidgetId}
            onSelectWidget={setSelectedWidgetId}
          />
        )}

        {step === 'selectors' && <DashboardFilters config={config} onChange={setConfig} />}

        {step === 'layout' && (
          <DashboardLayout
            config={config}
            onChange={setConfig}
            selectedWidgetId={selectedWidgetId}
            onSelectWidget={setSelectedWidgetId}
          />
        )}

        {step === 'settings' && (
          <DashboardSettings
            name={title}
            onNameChange={setTitle}
            config={config}
            onChange={setConfig}
          />
        )}
      </main>

      {previewing && (
        <DashboardPreview
          title={title}
          config={config}
          onClose={() => setPreviewing(false)}
        />
      )}

      {notice && (
        <div
          role="status"
          className={`fixed bottom-6 left-1/2 z-[9600] flex -translate-x-1/2 items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium shadow-2xl ${
            notice.kind === 'ok' ? 'bg-slate-900 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {notice.kind === 'ok' && <Check className="h-4 w-4" />}
          {notice.text}
          <button
            type="button"
            onClick={() => setNotice(null)}
            aria-label="Dismiss"
            className="ml-2 text-white/70 transition hover:text-white"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
